/**
 * DomainesRecentesSection.tsx
 * — barre CC/CA : teal au lieu de violet
 */

import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { FicheCard } from '@/components/jurisprudence/ficheCard';
import type { PublicStats, DomaineDetail } from '@/hooks/useStats';
import type { PublicFiche } from '@/components/jurisprudence/types';

const DOMAINE_ICONS: Record<string, string> = {
  'droit social':        '👷',
  'droit civil':         '⚖️',
  'droit commercial':    '🏢',
  'droit pénal':         '🔒',
  'procédure civile':    '📋',
  'droit fiscal':        '💶',
  'droit administratif': '🏛️',
  'droit immobilier':    '🏘️',
  'droit de la famille': '👨‍👩‍👧',
  'droit maritime':      '⚓',
};

interface DomainesRecentesSectionProps {
  stats?:  PublicStats | null;
  recent:  PublicFiche[];
}

const DomaineCard = ({ detail, isCc }: { detail: DomaineDetail; isCc: boolean }) => {
  const { t } = useTranslation();
  const locale = getDateLocale();
  const total = detail.count_cc + detail.count_ca;
  const pctCc = total > 0 ? Math.round((detail.count_cc / total) * 100) : 0;
  const pctCa = 100 - pctCc;
  const lastDate = detail.last_date
    ? new Date(detail.last_date).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const count = isCc ? detail.count_cc : detail.count_ca;

  return (
    <Link
      to={`/browse?domaine=${encodeURIComponent(detail.domaine)}`}
      className="rounded-xl border bg-card p-3.5 hover:bg-accent hover:border-primary/30 transition-all group flex flex-col gap-2"
    >
      <div className="flex items-start gap-2">
        <span className="text-lg">{DOMAINE_ICONS[detail.domaine.toLowerCase()] ?? '📁'}</span>
        <div className="min-w-0">
          <p className="text-[12px] font-semibold group-hover:text-primary transition-colors line-clamp-1">
            {detail.domaine}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {count.toLocaleString(locale)} {isCc ? t('jurisprudence.domaines.unit_rulings') : t('jurisprudence.domaines.unit_decisions')}
          </p>
        </div>
      </div>

      {/* Barre CC / CA — bleu primary + teal, pas de violet */}
      <div className="space-y-1">
        <div className="flex h-1 rounded-full overflow-hidden bg-muted">
          {pctCc > 0 && <div className="h-full bg-primary" style={{ width: `${pctCc}%` }} />}
          {pctCa > 0 && <div className="h-full bg-[#0e7490]" style={{ width: `${pctCa}%` }} />}
        </div>
        <div className="flex justify-between text-[9px] text-muted-foreground">
          <span>CC · {detail.count_cc.toLocaleString(locale)}</span>
          <span>CA · {detail.count_ca.toLocaleString(locale)}</span>
        </div>
      </div>

      {lastDate && (
        <p className="text-[9px] text-muted-foreground/60 border-t pt-1 mt-auto">
          {t('jurisprudence.domaines.last_date', { date: lastDate })}
        </p>
      )}
    </Link>
  );
};

export const DomainesRecentesSection = ({ stats, recent }: DomainesRecentesSectionProps) => {
  const { t } = useTranslation();
  const sliderRef = useRef<HTMLDivElement>(null);
  const domaines = stats?.domaines_detail ?? [];
  const domainesCount = domaines.length || stats?.domaines?.length || 0;

  const scroll = (dir: 'left' | 'right') => {
    if (!sliderRef.current) return;
    sliderRef.current.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' });
  };

  return (
    <section className="py-20 bg-background border-t border-border">
      <div className="max-w-[1100px] mx-auto px-6 sm:px-8">

        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <span className="block text-[10px] font-bold uppercase tracking-[.14em] text-primary mb-2.5">
              {domainesCount > 0 ? t('jurisprudence.domaines.covered', { n: domainesCount }) : t('jurisprudence.domaines.title_fallback')}
            </span>
            <h2 className="font-serif text-[30px] sm:text-[34px] font-bold text-foreground tracking-[-0.03em] leading-tight">
              {t('jurisprudence.domaines.h2_1')}<br />
              <em className="not-italic text-primary">{t('jurisprudence.domaines.h2_2')}</em>
            </h2>
          </div>
        </div>

        {/* Légende barre CC/CA — teal au lieu de violet */}
        {domaines.length > 0 && (
          <div className="flex items-center gap-4 mb-5 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-primary inline-block" />
              {t('jurisprudence.domaines.legend_cc')}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#0e7490] inline-block" />
              {t('jurisprudence.domaines.legend_ca')}
            </span>
          </div>
        )}

        {domaines.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-12">
            {domaines.slice(0, 8).map(d => (
              <DomaineCard key={d.domaine} detail={d} isCc={d.count_cc >= d.count_ca} />
            ))}
          </div>
        )}

        <div className="flex justify-center mb-12">
          <Link to="/browse"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-primary text-primary text-[13px] font-semibold hover:bg-primary hover:text-primary-foreground transition-all">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <line x1="4" y1="6" x2="20" y2="6" strokeWidth={2} strokeLinecap="round"/>
              <line x1="8" y1="12" x2="16" y2="12" strokeWidth={2} strokeLinecap="round"/>
              <line x1="10" y1="18" x2="14" y2="18" strokeWidth={2} strokeLinecap="round"/>
            </svg>
            {t('jurisprudence.domaines.advanced_cta')}
          </Link>
        </div>

        {recent.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-[11px] font-bold uppercase tracking-[.06em] text-foreground">
                {t('jurisprudence.domaines.latest')}
              </p>
              <div className="flex items-center gap-2">
                <button onClick={() => scroll('left')}
                  className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:bg-accent transition-colors"
                  aria-label={t('common.previous')}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
                  </svg>
                </button>
                <button onClick={() => scroll('right')}
                  className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:bg-accent transition-colors"
                  aria-label={t('common.next')}>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
                <Link to="/browse"
                  className="text-[11px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 ml-1">
                  {t('common.see_all')}
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                  </svg>
                </Link>
              </div>
            </div>

            <div ref={sliderRef}
              className="flex gap-3 overflow-x-auto scroll-smooth pb-3"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {recent.map(f => (
                <div key={f.id} className="flex-shrink-0 w-[320px] sm:w-[360px] flex flex-col h-full">
                  <FicheCard fiche={f} compact />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};